import clsx from 'clsx';
import type { FC } from 'react';
import { ArrowRight, Clock } from 'react-feather';

import Dialog from '@/componentsv2/Dialog';

export interface ReadyToSourceModalProps {
  open: boolean;
  submitting: boolean;
  onSaveDraft: () => void;
  onSubmitForSourcing: () => void;
  onClose: () => void;
}

// Copy is owner-signed-off against the team-reviewed prototype (ReadySubmit.jsx).
const COPY = {
  title: 'Ready to start sourcing?',
  body: 'If your expert request scope is complete, submit it for sourcing. If you need more time, save it as a draft and submit it later.',
  submit: {
    title: 'Submit for sourcing',
    desc: 'The OnFrontiers team is notified and starts sourcing experts.',
  },
  draft: {
    title: 'Save as draft',
    desc: 'Keeps it as a placeholder. No experts are sourced until you submit it.',
  },
  footerNote:
    'Either way, you can invite teammates next, and the OnFrontiers team finalizes scope before sourcing experts.',
};

interface OptionCardProps {
  icon: FC<{ className?: string }>;
  title: string;
  desc: string;
  primary?: boolean;
  disabled: boolean;
  onClick: () => void;
}

const OptionCard = ({
  icon: Icon,
  title,
  desc,
  primary = false,
  disabled,
  onClick,
}: OptionCardProps) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={clsx(
      'flex items-center gap-16 rounded-sm border p-16 text-left transition',
      'disabled:cursor-not-allowed disabled:opacity-60',
      '[&:not(:disabled):hover]:shadow-1',
      primary
        ? 'border-brand-primary bg-brand-primary text-surface-card [&:not(:disabled):hover]:bg-brand-primary-hover'
        : 'border-on-surface-muted [&:not(:disabled):hover]:border-brand-primary'
    )}
  >
    <span
      className={clsx(
        'flex h-40 w-40 shrink-0 items-center justify-center rounded-sm',
        primary ? 'bg-surface-card text-brand-primary' : 'bg-surface-muted text-on-surface'
      )}
    >
      <Icon className="h-20 w-20" aria-hidden="true" />
    </span>
    <span className="flex flex-col gap-4">
      <span
        className={clsx('text-16 font-bold', primary ? 'text-surface-card' : 'text-on-surface')}
      >
        {title}
      </span>
      <span
        className={clsx(
          'text-14',
          primary ? 'text-surface-card opacity-90' : 'text-on-surface-secondary'
        )}
      >
        {desc}
      </span>
    </span>
  </button>
);

/**
 * Post-create decision modal (ENG-5313 T4). Shown after the initial form, before
 * inviting teammates. Two explicit choices: submit for sourcing (draft -> active)
 * or save as a draft. Controlled and side-effect-free — the page owns the create
 * mutation, navigation, and the `submitting` flag.
 */
export function ReadyToSourceModal({
  open,
  submitting,
  onSaveDraft,
  onSubmitForSourcing,
  onClose,
}: ReadyToSourceModalProps): JSX.Element {
  return (
    // componentsv2/Dialog silently drops the maxWidth/PaperProps it's passed, so widen the
    // paper via its class here. Follow-up: teach the Dialog primitive to forward maxWidth.
    <Dialog
      open={open}
      onClose={onClose}
      disablePortal={false}
      className="[&_.MuiDialog-paper]:!max-w-[680px]"
    >
      <div data-testid="er-ready-to-source-modal" className="flex flex-col gap-16 p-24">
        <h2 className="hd-5">{COPY.title}</h2>
        <p className="text-16 leading-normal text-on-surface-secondary">{COPY.body}</p>
        <div className="flex flex-col gap-12">
          <OptionCard
            icon={ArrowRight}
            title={COPY.submit.title}
            desc={COPY.submit.desc}
            primary
            disabled={submitting}
            onClick={onSubmitForSourcing}
          />
          <OptionCard
            icon={Clock}
            title={COPY.draft.title}
            desc={COPY.draft.desc}
            disabled={submitting}
            onClick={onSaveDraft}
          />
        </div>
        <p className="text-center text-on-surface-secondary body-1">{COPY.footerNote}</p>
      </div>
    </Dialog>
  );
}
